import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { filter, switchMap, tap } from 'rxjs';

import { LocalStorageKeyEnum } from '@shared-enums/local-storage.enum';
import { checkLocalStorage, updateLocalStorage } from '@shared-helpers/storage-helpers';
import { safeCompare } from '@shared-helpers/string-number-helpers';
import { IHttpResponseEmployee, IWebServerResponseApprover } from '@shared-interfaces/api';

import { DatahubService } from '../services/datahub.service';
import { DateHandler } from './date-handler';

export class UserHandler {
  /** @description The ID for who you are "working as" */
  public approvingId = '';
  /** @description The Name for who you are "working as" */
  public approvingName = '';
  /** @description The Cost Center for who you are "working as" */
  public approvingCostCenter = '';
  /** @description The Cost Center for who you are "working as" */
  public employeeId = '';

  /** @description The TRUE Name of the logged in user */
  public userName = '';
  public costCentersForApproval: string[] = [];
  public reports: string[] = [];

  private readonly readySubject$ = new BehaviorSubject(false); //this subject fires an event when the application is ready to be rendered. This lets us wait for user required user data before rendering anything.
  public readonly ready$ = this.readySubject$.asObservable();
  private readonly authorizedSubject$ = new Subject<boolean>();
  public readonly authorized$ = this.authorizedSubject$.asObservable();

  public get unauthorizedErrorMessage() {
    return this._unauthorizedErrorMessage;
  }

  public get unauthorizedErrorEmailBody(): string {
    let msg = `

Do not change anything below this line
----------------------------------------------------
Error Message: ${this.unauthorizedErrorMessage}
Employee Number: ${this.id}`;
    if (this.userObj) {
      msg += `
Name: ${this.getFullName()}
Cost Center: ${this.getHomeCC()}`;
    }
    return msg;
  }

  /** @description The TRUE ID of the logged in user */
  private id = '';

  private userObj: IHttpResponseEmployee;
  private hasTimedOut = false;
  private counter?: ReturnType<typeof setTimeout>;
  private _unauthorizedErrorMessage = //this is the default error message, but ideally it will never be seen since when certain things happen more specific messages are shown
    'Your employee number is either invalid in SAP, or you are not setup to approve any cost centers in Davey Time.';
  private readonly timeoutMs = 3600000 * 26; //26 hours represented in milliseconds

  constructor(private readonly datahub: DatahubService) {
    this.datahub.apiService
      .getSelf$()
      .pipe(
        tap((value) => {
          this.id = value.EmployeeNum;
          if (!this.id) {
            throw new Error('You have no employee number!');
          }
        }),
        switchMap(() => {
          const user = checkLocalStorage<IHttpResponseEmployee>(LocalStorageKeyEnum.User);
          if (user && safeCompare(user.EmpId ?? '', this.id)) {
            this.userObj = user;
            return this.switchApprover$(this.id);
          } else {
            return this.datahub.apiService.getOneEmployee$(new DateHandler().getSapDateString(), this.id).pipe(
              tap((self) => {
                if (self) {
                  this.userObj = self;
                } else {
                  throw new Error('Your account is not properly set up in SAP!');
                }
              }),
              switchMap(() => this.switchApprover$(this.id)),
            );
          }
        }),
      )
      .subscribe({
        next: (newApprover) => {
          this.handleFinished(newApprover);
        },
        error: (err) => {
          this.handleUnauthorized(err.message || 'There was an error getting your authorization info for Davey Time approvals!');
          console.error('Issue getting employee information\n', err);
        },
      });

    this.handleActivity();
  }

  public switchApprover$(empno: string): Observable<IWebServerResponseApprover> {
    return this.datahub.approvers.getApprover$(empno).pipe(
      filter((approver) => approver != null), //remove empty items
      tap((approver) => {
        //Update some properties locally when switching, but also return the new approver data to subscribers
        this.approvingCostCenter = approver.homeCostCenter;
        this.approvingId = approver.value;
        this.costCentersForApproval = approver.costCenters;
        this.reports = approver.reports;
        this.approvingName = approver.display;
      }),
    );
  }

  public getApprover(): string {
    return this.approvingId;
  }

  public getEmpId(): string {
    return this.userObj.EmpId;
  }

  public getHomeCC(): string {
    return this.userObj.CostCenter;
  }

  public getName(): string {
    return this.userObj.FirstName;
  }

  public getFullName(): string {
    return `${this.userObj.FirstName} ${this.userObj.LastName}`;
  }

  public getApprovingName(): string {
    return this.approvingName;
  }

  public getPayType(): string {
    return this.userObj.TimeMngtText ?? '';
  }

  public getWorkScheduleRule(): string {
    return this.userObj.WorkScheduleRule ?? '';
  }

  public isDirectReport(empId: string): boolean {
    return this.reports.some((x) => safeCompare(empId, x));
  }

  private handleUnauthorized(errorMsg: string, employeeId = 'None Provided'): void {
    this._unauthorizedErrorMessage = errorMsg;
    this.employeeId = employeeId;
    this.authorizedSubject$.next(false);
    this.readySubject$.next(true);
  }

  private handleFinished(approver: IWebServerResponseApprover | undefined): void {
    if (!approver) {
      this.handleUnauthorized('There was an error getting your authorization info for Davey Time approvals!');
    } else if (this.costCentersForApproval.length === 0) {
      //This happens when the current user has no entries in the ZCATSAPPROVERS table
      this.handleUnauthorized('You are not set up as an approver for the General Foreman application!');
    } else {
      this.datahub.logRocket.identify(this.id, this.getFullName(), this.getHomeCC());
      updateLocalStorage(LocalStorageKeyEnum.User, this.userObj);
      updateLocalStorage(LocalStorageKeyEnum.UserId, this.id);
      this.userName = this.getFullName();
      this.datahub.approvers.restrictList(this.costCentersForApproval);
      this.authorizedSubject$.next(true);
      this.readySubject$.next(true);
    }
  }

  private handleActivity(): void {
    if (this.hasTimedOut) {
      this.signOut();
      return;
    }
    clearTimeout(this.counter);
    this.counter = setTimeout(() => {
      this.handleTimeout();
    }, this.timeoutMs);
  }

  private handleTimeout(): void {
    this.hasTimedOut = true;
  }

  private signOut(): void {
    this.datahub.apiService.signOut$().subscribe({
      next: (response) => {
        if (response) {
          this.datahub.messageService.info('You have been signed out due to inactivity.');
        }
      },
      error: (err) => {
        console.error('Error signing out:', err);
      },
    });
  }
}
